import { useState, useEffect, useCallback, memo, ReactNode } from 'react';
import { Box, Container, Typography, Button, Stack, SxProps, Theme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface HeroProps {
  title?: string;
  subtitle?: string;
  tagline?: string;
  taglineSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  subtitleMb?: number;
  childrenMt?: number;
  primaryCta?: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
  centered?: boolean;
  variant?: 'default' | 'gradient';
  showScrollIndicator?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  sx?: SxProps<Theme>;
  children?: ReactNode;
  renderTitle?: (title: string) => ReactNode;
}

function HeroComponent({
  title,
  subtitle,
  tagline,
  taglineSx,
  titleSx,
  subtitleMb = 4,
  childrenMt,
  primaryCta,
  secondaryCta,
  centered = true,
  variant = 'default',
  showScrollIndicator = false,
  maxWidth = 'lg',
  sx,
  children,
  renderTitle,
}: HeroProps) {
  const [indicatorVisible, setIndicatorVisible] = useState(true);

  // Memoized scroll handler
  const handleScroll = useCallback(() => {
    if (window.scrollY > 100) {
      setIndicatorVisible(false);
    }
  }, []);

  useEffect(() => {
    if (!showScrollIndicator) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showScrollIndicator, handleScroll]);

  const handleScrollClick = useCallback(() => {
    setIndicatorVisible(false);
    window.scrollBy({ top: window.innerHeight * 0.7, behavior: 'smooth' });
  }, []);

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        ...(variant === 'gradient' && {
          background:
            'linear-gradient(135deg, rgba(77, 101, 255, 0.08) 0%, rgba(14, 165, 223, 0.08) 100%)',
        }),
        ...sx,
      }}
    >
      <Container maxWidth={maxWidth}>
        {children ? (
          <Box sx={{ mt: childrenMt }}>
            {tagline && (
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 2,
                  mb: 2,
                  display: 'block',
                  textAlign: centered ? 'center' : 'left',
                  ...taglineSx,
                }}
              >
                {tagline}
              </Typography>
            )}
            {title && (
              renderTitle ? renderTitle(title) : (
                <Typography variant="h1" sx={{ mb: 3, textAlign: centered ? 'center' : 'left', ...titleSx }}>
                  {title}
                </Typography>
              )
            )}
            {children}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: centered ? 'center' : 'left',
              maxWidth: centered ? 800 : 600,
              mx: centered ? 'auto' : 0,
            }}
          >
            {tagline && (
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 2,
                  mb: 2,
                  display: 'block',
                  ...taglineSx,
                }}
              >
                {tagline}
              </Typography>
            )}
            {title && (
              renderTitle ? renderTitle(title) : (
                <Typography variant="h1" sx={{ mb: 3, ...titleSx }}>
                  {title}
                </Typography>
              )
            )}
            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  mb: subtitleMb,
                  color: 'text.secondary',
                  fontSize: '1.25rem',
                }}
              >
                {subtitle}
              </Typography>
            )}
            {(primaryCta || secondaryCta) && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent={centered ? 'center' : 'flex-start'}
              >
                {primaryCta && (
                  <Button
                    variant="contained"
                    size="large"
                    component="a"
                    href={primaryCta.href}
                    sx={{ px: 4 }}
                  >
                    {primaryCta.label}
                  </Button>
                )}
                {secondaryCta && (
                  <Button
                    variant="outlined"
                    size="large"
                    component="a"
                    href={secondaryCta.href}
                    sx={{ px: 4 }}
                  >
                    {secondaryCta.label}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        )}
      </Container>

      {showScrollIndicator && indicatorVisible && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 4,
            cursor: 'pointer',
            opacity: 0.6,
            transition: 'opacity 0.3s',
            '&:hover': { opacity: 1 },
          }}
          onClick={handleScrollClick}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Scroll for more
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              color: 'text.secondary',
              fontSize: 28,
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                '40%': { transform: 'translateY(6px)' },
                '60%': { transform: 'translateY(3px)' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// Memoize to prevent unnecessary re-renders
export const Hero = memo(HeroComponent);
